import type { GridColDef } from '@mui/x-data-grid';
import type { ReportData } from '@weft/types';
import type { ValueOf } from '@weft/utils';
import { isEmptyString, isNullOrUndefined } from '@weft/utils';
import { ReportJobStatusChip } from 'apps/app/src/components/design/ReportJobStatusChip';
import { compareDatesDescending, formatDate } from 'apps/app/src/utils/date';
import type { ReportJobsDownloadProps } from '../ReportJobsSection';
import { DeleteReportButton } from './DeleteReportButton';
import { DownloadReportButton } from './DownloadReportButton';
import { OpenReportButton } from './OpenReportButton';

export const REPORT_DATA_KEYS = Object.freeze({
  STATUS: 'status',
  NAME: 'reportName',
  CREATED_AT: 'createdAt',
  INVOCATION_TYPE: 'invocationType',
  DELETE_REPORT: 'deleteReport',
  OPEN_REPORT: 'openReport',
} satisfies Record<string, keyof ReportData | 'deleteReport' | 'openReport'>);

export type ReportJobsColumnKeys = ValueOf<typeof REPORT_DATA_KEYS>;

export const getTableCell = (value: string | number) => {
  return (
    <div className="flex items-center justify-center">
      <p className="font-sans text-black">{value}</p>
    </div>
  );
};

export const getReportJobsColumns = (handleDelete: (reportJobId: string) => void, onDownload?: ReportJobsDownloadProps['onDownload']): GridColDef<ReportData>[] => [
  {
    field: REPORT_DATA_KEYS.STATUS,
    headerName: 'Status',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    cellClassName: 'pl-10',
    width: 160,
    renderCell: ({ row }) => <ReportJobStatusChip status={row.status}/>,
  },
  {
    field: REPORT_DATA_KEYS.NAME,
    headerName: 'Report name',
    headerClassName: 'text-sm',
    flex: 2,
    headerAlign: 'center',
    align: 'center',
    renderCell: ({ row: { reportName } }) => getTableCell(isEmptyString(reportName) ? '--' : reportName),
  },
  {
    field: REPORT_DATA_KEYS.CREATED_AT,
    headerName: 'Created at',
    headerClassName: 'text-sm',
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row }) => formatDate(row.createdAt),
    sortComparator: (createdAt1: Date, createdAt2: Date) => compareDatesDescending(createdAt2, createdAt1),
  },
  {
    field: REPORT_DATA_KEYS.INVOCATION_TYPE,
    headerName: 'Creation method',
    headerClassName: 'text-sm',
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row: { invocationType } }) => getTableCell(invocationType),
  },
  {
    field: REPORT_DATA_KEYS.OPEN_REPORT,
    headerName: '',
    flex: 1,
    sortable: false,
    align: 'right',
    renderCell: ({ row: { status, id, type } }) => 
      isNullOrUndefined(onDownload) ? 
        <OpenReportButton
          reportId={id}
          status={status}
          type={type}
        />
        : <DownloadReportButton onClick={() => onDownload(id)}/>,
  },
  {
    field: REPORT_DATA_KEYS.DELETE_REPORT,
    headerName: '',
    sortable: false,
    width: 50,
    align: 'center',
    renderCell: ({ row: { id, reportName } }) => (
      <DeleteReportButton
        onDelete={() => handleDelete(id)}
        reportName={reportName}
      />
    ),
  },
];
